import { useState } from 'preact/hooks';
import { cn } from '@/lib/cn';
import { initials } from '@/lib/format';

export type AvatarSize = 'xs' | 'sm' | 'md' | 'lg' | 'xl';

export interface AvatarProps {
  name?: string | null;
  src?: string | null;
  size?: AvatarSize;
  variant?: 'neutral' | 'brand' | 'gradient';
  className?: string;
}

const SIZE: Record<AvatarSize, string> = {
  xs: 'h-5 w-5 text-3xs',
  sm: 'h-7 w-7 text-2xs',
  md: 'h-9 w-9 text-xs',
  lg: 'h-12 w-12 text-sm',
  xl: 'h-16 w-16 text-base',
};

const VARIANT: Record<NonNullable<AvatarProps['variant']>, string> = {
  neutral: 'bg-surface-2 text-fg-muted',
  brand: 'bg-brand-tint text-brand',
  gradient: 'bg-gradient-to-br from-brand to-info text-brand-contrast',
};

export function Avatar({ name, src, size = 'md', variant = 'gradient', className }: AvatarProps) {
  const [broken, setBroken] = useState(false);
  const label = initials(name);
  const showImage = !!src && !broken;
  return (
    <span
      role="img"
      aria-label={name ?? 'avatar'}
      className={cn(
        'inline-flex shrink-0 items-center justify-center overflow-hidden rounded-full font-semibold uppercase tracking-wide ring-1 ring-line-subtle',
        SIZE[size],
        !showImage && VARIANT[variant],
        className,
      )}
    >
      {showImage ? (
        <img
          src={src}
          alt=""
          onError={() => setBroken(true)}
          className="h-full w-full object-cover"
        />
      ) : (
        label
      )}
    </span>
  );
}


